/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
import React from "react";
import { makeStyles } from "@material-ui/styles";
import {
  InputBase,
  FormControl,
  Select,
  MenuItem,
  Grid,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#ffffff",
    borderRadius: 4,
    border: "1px solid #e0e0e0",
    padding: theme.spacing(0, 1),
    height: 48,
  },
  searchIcon: {
    color: "#757575",
    marginRight: theme.spacing(1),
  },
  input: {
    flex: 1,
    fontSize: "0.9rem",
  },
  filterContainer: {
    position: "relative",
  },
  select: {
    backgroundColor: "#ffffff",
    borderRadius: 4,
    border: "1px solid #e0e0e0",
    padding: theme.spacing(0, 1),
    height: 48,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& .MuiSelect-select": {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      fontSize: "0.9rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

/**
 * A component that displays a search bar and select menus for filtering
 * training modules by status, category, and supplier.
 *
 * @param {object} props - The component props
 * @param {string} props.searchQuery - The current search query string.
 * @param {Function} props.onSearchChange - A callback function to call when the search query changes.
 * @param {string} props.statusFilter - The current status filter value.
 * @param {Function} props.onStatusFilterChange - A callback function to call when the status filter changes.
 * @param {string} props.categoryFilter - The current category filter value.
 * @param {Function} props.onCategoryFilterChange - A callback function to call when the category filter changes.
 * @param {string} props.supplierFilter - The current supplier filter value.
 * @param {Function} props.onSupplierFilterChange - A callback function to call when the supplier filter changes.
 * @param {Array} props.categories - An array of objects with 'value' and 'label' properties representing the available categories.
 * @param {Array} props.suppliers - An array of objects with 'value' and 'label' properties representing the available suppliers.
 */
function FilterBar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  supplierFilter,
  onSupplierFilterChange,
  categories,
  suppliers,
}) {
  const classes = useStyles();

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12} md={4}>
        <div className={classes.searchContainer}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.input}
            placeholder="Search modules..."
            inputProps={{ "aria-label": "search modules" }}
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            fullWidth
          />
        </div>
      </Grid>

      <Grid item xs={12} md={2} className={classes.filterContainer}>
        <FormControl fullWidth variant="outlined">
          <Select
            id="status-select"
            value={statusFilter}
            onChange={e => onStatusFilterChange(e.target.value)}
            className={classes.select}
            displayEmpty
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="passed">Passed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="not-started">Not Started</MenuItem>
            <MenuItem value="required">Required</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3} className={classes.filterContainer}>
        <FormControl fullWidth variant="outlined">
          <Select
            id="category-select"
            value={categoryFilter}
            onChange={e => onCategoryFilterChange(e.target.value)}
            className={classes.select}
            displayEmpty
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map(category => (
              <MenuItem key={category.value} value={category.value}>
                {category.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3} className={classes.filterContainer}>
        <FormControl fullWidth variant="outlined">
          <Select
            id="supplier-select"
            value={supplierFilter}
            onChange={e => onSupplierFilterChange(e.target.value)}
            className={classes.select}
            displayEmpty
          >
            <MenuItem value="all">All Suppliers</MenuItem>
            {suppliers.map(supplier => (
              <MenuItem key={supplier.value} value={supplier.value}>
                {supplier.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
}

export default FilterBar;
